import type moment from 'moment'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Hr from 'components/Common/Hr'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import SolidBuildingIcon from 'LuxKit/Icons/solid/SolidBuildingIcon'
import FlexibleCancellationCharterFlightFees from './FlexibleCancellationCharterFlightFees'
import { TimelineNode, TimelineEndNode } from './CancellationPolicyTimelineComponents'
import {
  formatFlexibleCancellationPolicyDate,
  getFlexibleCancellationTimelinePolicyDates,
} from './flexibleCancellationPolicyTimelineUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'

interface Props {
  today: moment.Moment
  checkInDate: string | moment.Moment
  startBasedOnToday: moment.Moment
  startBasedOnCheckIn: moment.Moment
  cancellationAfterChangeOfMindPeriod: boolean
  /** Whether this is a charter bundle (flights + hotel). Affects terminology and shows charter-specific fees. */
  isCharter?: boolean
}

function FlexibleCancellationPolicyModalWithDates(props: Props) {
  const {
    today,
    checkInDate,
    startBasedOnToday,
    startBasedOnCheckIn,
    cancellationAfterChangeOfMindPeriod,
    isCharter,
  } = props

  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()

  const {
    fullRefundDate,
    partialRefundDate,
    partialRefundDeadline,
    noRefundDate,
  } = getFlexibleCancellationTimelinePolicyDates({
    startBasedOnToday,
    startBasedOnCheckIn,
  })

  const formatDateForPolicy = useCallback((date: string | moment.Moment) => formatFlexibleCancellationPolicyDate(
    date,
    today,
    intlDateFormatter,
  ), [intlDateFormatter, today])

  const checkInDateLabel = isCharter
    ? intl.formatMessage(
        {
          id: 'jdUCuaxcQF-NeXnLagHX',
          defaultMessage: '{date} · Departure',
          description: 'Check-in date with departure label on cancellation timeline (charter)',
        },
        { date: formatDateForPolicy(checkInDate) },
      )
    : intl.formatMessage(
        {
          id: 'd59NymZ4PuZiqmggDhvS',
          defaultMessage: '{date} · Check-in',
          description: 'Check-in date with check-in label on cancellation timeline (non-charter)',
        },
        { date: formatDateForPolicy(checkInDate) },
      )

  const isPartialRefundToday = partialRefundDate.isSame(today, 'day')

  const upToDateLabel = intl.formatMessage(
    {
      id: 'l3pEXYwwkXfvG_HOiItm',
      defaultMessage: 'Up to {date}',
      description: 'Date label showing the end of the full refund period in cancellation timeline',
    },
    { date: formatDateForPolicy(partialRefundDate) },
  )

  const upToDateTodayLabel = intl.formatMessage(
    {
      id: '3WWQUkwCPrh4pVS0MjbK',
      defaultMessage: 'Up to {date} · Today',
      description: 'Date label showing end of full refund period when it is today in cancellation timeline',
    },
    { date: formatDateForPolicy(partialRefundDate) },
  )

  const partialRefundDateTodayLabel = intl.formatMessage(
    {
      id: 'J2kDJu22K6pdAWrxdqQV',
      defaultMessage: '{date} · Today',
      description: 'Partial refund date with today indicator on cancellation timeline',
    },
    { date: formatDateForPolicy(partialRefundDate) },
  )

  return (
    <>
      <Group direction="vertical">
        <TimelineNode
          label={isPartialRefundToday ? upToDateTodayLabel : upToDateLabel}
        >
          <BodyText variant="medium" weight="bold" colour="highlight-primary-dark">
            {intl.formatMessage({
              id: 'IPvnv3wz6i5O1pItlNwB',
              defaultMessage: 'Full refund',
              description: 'Section heading for full refund in no-dates cancellation modal',
            })}
          </BodyText>
          <BodyText variant="medium" as="p">
            {isCharter && intl.formatMessage({
              id: 'jhe_gCUlHNGZxsCU6At0',
              defaultMessage: 'Cancel before {date}, 11:59 pm to get full refund to your original payment method or credit.',
              description: 'Full refund body in with-dates cancellation modal (charter)',
            }, { date: formatDateForPolicy(fullRefundDate) })}
            {!isCharter && intl.formatMessage({
              id: 'YbSKjtELmIvBoE3NWzzr',
              defaultMessage: 'Cancel before {date}, 11:59 pm to get full refund of accommodation purchase price to your original payment method or credit.',
              description: 'Full refund body in with-dates cancellation modal (non-charter)',
            }, { date: formatDateForPolicy(fullRefundDate) })}
          </BodyText>
        </TimelineNode>

        {cancellationAfterChangeOfMindPeriod && !isCharter && (
          <TimelineNode
            label={isPartialRefundToday ? partialRefundDateTodayLabel : formatDateForPolicy(partialRefundDate)}
          >
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'BkK38vGr7Mr942F0vUUA',
                defaultMessage: 'Full refund to credit',
                description: 'Section heading for full credit refund in cancellation timeline modal',
              })}
            </BodyText>
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'NMl5qQRos3BLleHcfVU-',
                defaultMessage: 'Cancel before {date}, 11:59 pm to receive 100% refund to credit.',
                description: 'Credit-only refund for non-charter with date in the cancellation flow',
              }, { date: formatDateForPolicy(partialRefundDeadline) })}
            </BodyText>
          </TimelineNode>
        )}

        {cancellationAfterChangeOfMindPeriod && isCharter && (
          <TimelineNode
            label={isPartialRefundToday ? partialRefundDateTodayLabel : formatDateForPolicy(partialRefundDate)}
          >
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'KsmgOLy4pOps0C4ljO4T',
                defaultMessage: 'Partial refund',
                description: 'Section heading for partial/credit refund in a modal dialog',
              })}
            </BodyText>
            <Group direction="vertical" gap={8}>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'UCWntp5VmYKW3qLQ9ovG',
                  defaultMessage: 'Cancel before {date}, 11:59 pm to get a partial refund to your original payment method or credit. Cancellation fees apply:',
                  description: 'Partial refund intro for charter with date in the payment section',
                }, { date: formatDateForPolicy(partialRefundDeadline) })}
              </BodyText>
              <FlexibleCancellationCharterFlightFees />
            </Group>
          </TimelineNode>
        )}

        <TimelineNode label={formatDateForPolicy(noRefundDate)}>
          <BodyText variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'yAazKuRHWgNT7wkPi4c9',
              defaultMessage: 'No refund',
              description: 'Section heading for no-refund period in a modal dialog',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'ah8w_Ec4UjW_D49bKyOc',
              defaultMessage: 'Cancellations after {date}, 11:59 pm are non-refundable.',
              description: 'No refund body in with-dates cancellation modal in a modal dialog',
            }, { date: formatDateForPolicy(partialRefundDeadline) })}
          </BodyText>
        </TimelineNode>

        <TimelineEndNode label={checkInDateLabel} icon={!isCharter ? SolidBuildingIcon : undefined} />
      </Group>

      <Hr size="md" />

      <div>
        <BodyText variant="medium" weight="bold">
          {intl.formatMessage({
            id: 'zOgZxgWqO_0Kba00Iaqk',
            defaultMessage: 'Date changes',
            description: 'Section heading for date changes in the date selection or calendar picker',
          })}
        </BodyText>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: '7faDfrrNU0w8A0OdSY9Z',
            defaultMessage: 'One change permitted between {startDate} and {endDate}. Subject to availability and applicable surcharges (peak periods or property-specific).',
            description: 'Date change policy text with date range in the date selection or calendar picker',
          }, {
            startDate: formatDateForPolicy(fullRefundDate),
            endDate: partialRefundDeadline ? formatDateForPolicy(partialRefundDeadline) : '',
          })}
        </BodyText>
      </div>
    </>
  )
}

export default FlexibleCancellationPolicyModalWithDates
